import React, { useState } from 'react';
import { Mail, MapPin, AlertCircle, CheckCircle2, Phone, Accessibility } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    phone: '',
    email: '',
    message: '',
    honeypot: '' // Spam protection field
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Please enter your name';
    }
    
    if (!formData.organization.trim()) {
      newErrors.organization = 'Please enter your organization';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Please tell us how we can help';
    }
    
    // Optional: validate email if provided
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setSubmitStatus(null);

    try {
      // Submit to real backend API
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        organization: formData.organization,
        email: formData.email || null,
        phone: formData.phone || null,
        message: formData.message,
        honeypot: formData.honeypot,
        source_page: window.location.pathname
      });

      setSubmitStatus({ type: 'success', message: response.data.message });
      setFormData({ name: '', organization: '', phone: '', email: '', message: '', honeypot: '' });
    } catch (error) {
      let errorMessage = 'Something went wrong. Please try emailing us directly at hello@lonestarrepublicco.com';
      
      if (error.response?.status === 429) {
        errorMessage = 'Too many submissions. Please try again in an hour or email us directly.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      setSubmitStatus({ type: 'error', message: errorMessage });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-stone-100 py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-[#1e3a5f] mb-6">
            Get in Touch
          </h1>
          <p className="text-xl text-gray-700 leading-relaxed">
            Tell us what you're trying to figure out. We'll respond personally.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl font-bold text-[#1e3a5f] mb-6">
                Start a Conversation
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Fill out the form or email us directly. We'll get back to you within one business day.
              </p>
              <p className="text-gray-600 mb-8 leading-relaxed">
                You won't get a sales pitch or automated follow-ups. We read every message personally and respond directly.
              </p>

              <div className="space-y-6 mb-8">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#f5e6e0] rounded-lg flex items-center justify-center">
                    <Phone size={24} className="text-[#c84630]" />
                  </div>
                  <div className="ml-4">
                    <h3 className="font-semibold text-[#1e3a5f] mb-1">Phone</h3>
                    <a 
                      href="tel:325-305-2777"
                      className="text-gray-700 hover:text-[#c84630] transition-colors"
                    >
                      325-305-2777
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#f5e6e0] rounded-lg flex items-center justify-center">
                    <Mail size={24} className="text-[#c84630]" />
                  </div>
                  <div className="ml-4">
                    <h3 className="font-semibold text-[#1e3a5f] mb-1">Email</h3>
                    <a 
                      href="mailto:hello@lonestarrepublicco.com"
                      className="text-gray-700 hover:text-[#c84630] transition-colors"
                    >
                      hello@lonestarrepublicco.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#f5e6e0] rounded-lg flex items-center justify-center">
                    <MapPin size={24} className="text-[#c84630]" />
                  </div>
                  <div className="ml-4">
                    <h3 className="font-semibold text-[#1e3a5f] mb-1">Location</h3>
                    <p className="text-gray-700">
                      Serving Abilene and West Texas
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-stone-50 p-6 rounded-lg border-l-4 border-[#c84630]">
                <h3 className="font-semibold text-[#1e3a5f] mb-2">
                  What happens next?
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  We'll respond within one business day. Real person. No automated responses. We'll have a straightforward conversation about whether we can help.
                </p>
              </div>

              {/* Accessibility Compliance Note */}
              <div className="mt-6 flex items-start text-gray-500">
                <Accessibility size={14} className="mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <p className="text-xs leading-relaxed">
                  This website meets TAC §206 and WCAG 2.2 Level AA accessibility standards. Public organizations can engage with us confidently.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-stone-50 p-8 rounded-lg border border-gray-200">
                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                  {/* Name Field */}
                  <div>
                    <Label htmlFor="name" className="text-[#1e3a5f] font-medium">
                      Your Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      className={`mt-2 ${errors.name ? 'border-red-500' : ''}`}
                      placeholder="John Smith"
                      aria-invalid={errors.name ? "true" : "false"}
                      aria-describedby={errors.name ? "name-error" : undefined}
                      aria-required="true"
                      autoComplete="name"
                    />
                    {errors.name && (
                      <p id="name-error" role="alert" className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle size={14} className="mr-1" aria-hidden="true" />
                        {errors.name}
                      </p>
                    )}
                  </div>

                  {/* Organization Field */}
                  <div>
                    <Label htmlFor="organization" className="text-[#1e3a5f] font-medium">
                      Organization *
                    </Label>
                    <Input
                      id="organization"
                      name="organization"
                      type="text"
                      value={formData.organization}
                      onChange={handleChange}
                      className={`mt-2 ${errors.organization ? 'border-red-500' : ''}`}
                      placeholder="Your business or organization name"
                      aria-invalid={errors.organization ? "true" : "false"}
                      aria-describedby={errors.organization ? "organization-error" : undefined}
                      aria-required="true"
                      autoComplete="organization"
                    />
                    {errors.organization && (
                      <p id="organization-error" role="alert" className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle size={14} className="mr-1" aria-hidden="true" />
                        {errors.organization}
                      </p>
                    )}
                  </div>

                  {/* Email Field (optional) */}
                  <div>
                    <Label htmlFor="email" className="text-[#1e3a5f] font-medium">
                      Your Email (optional)
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`mt-2 ${errors.email ? 'border-red-500' : ''}`}
                      placeholder="you@example.com"
                      aria-invalid={errors.email ? "true" : "false"}
                      aria-describedby={errors.email ? "email-error" : undefined}
                      autoComplete="email"
                    />
                    {errors.email && (
                      <p id="email-error" role="alert" className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle size={14} className="mr-1" aria-hidden="true" />
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* Phone Field */}
                  <div>
                    <Label htmlFor="phone" className="text-[#1e3a5f] font-medium">
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="mt-2"
                      placeholder="(325) 555-0123"
                      autoComplete="tel"
                    />
                  </div>

                  {/* Message Field */}
                  <div>
                    <Label htmlFor="message" className="text-[#1e3a5f] font-medium">
                      Tell us what you're trying to figure out. *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className={`mt-2 ${errors.message ? 'border-red-500' : ''}`}
                      placeholder="Tell us what you're trying to understand or what challenges you're facing..."
                      aria-invalid={errors.message ? "true" : "false"}
                      aria-describedby={errors.message ? "message-error" : undefined}
                      aria-required="true"
                    />
                    {errors.message && (
                      <p id="message-error" role="alert" className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle size={14} className="mr-1" aria-hidden="true" />
                        {errors.message}
                      </p>
                    )}
                  </div>

                  {/* Honeypot field - hidden from users, catches bots */}
                  <input
                    type="text"
                    name="honeypot"
                    value={formData.honeypot}
                    onChange={handleChange}
                    style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
                    tabIndex="-1"
                    autoComplete="off"
                    aria-hidden="true"
                  />

                  {/* Submit Status Messages - WCAG 4.1.3 Status Messages */}
                  {submitStatus && (
                    <div 
                      role="status"
                      aria-live="polite"
                      className={`p-4 rounded-lg ${
                        submitStatus.type === 'success' 
                          ? 'bg-green-50 text-green-800 border border-green-200' 
                          : 'bg-red-50 text-red-800 border border-red-200'
                      }`}
                    >
                      <div className="flex items-start">
                        {submitStatus.type === 'success' ? (
                          <CheckCircle2 size={20} className="mr-2 flex-shrink-0 mt-0.5" aria-hidden="true" />
                        ) : (
                          <AlertCircle size={20} className="mr-2 flex-shrink-0 mt-0.5" aria-hidden="true" />
                        )}
                        <p>{submitStatus.message}</p>
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#c84630] hover:bg-[#a83825] text-white font-medium py-6 text-lg min-h-[44px]"
                    aria-describedby="form-help-text"
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </Button>

                  <p id="form-help-text" className="text-sm text-gray-600 text-center">
                    Real person will respond. No automated messages.
                  </p>
                  <p className="text-sm text-gray-600 text-center mt-2">
                    If we're not the right fit, we'll say so.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
